import React, { useEffect, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine
} from 'recharts';
import Modal from '../components/Modal';

const badgeDomains = [
  { key: "motivation", label: "Motivation", imagePrefix: "motivation", subskills: ["autonomy", "relatedness", "competence"] },
  { key: "leadershipSkills", label: "Leadership Skills", imagePrefix: "leaderskills", subskills: ["activeListening", "microskillsFeedback", "problemSolving"] },
  { key: "leadershipStyle", label: "Leadership Style", imagePrefix: "leaderstyle", subskills: ["focused", "inspiring", "styleSummary"] },
  { key: "emotionalIntelligence", label: "Emotional Intelligence", imagePrefix: "ei", subskills: ["emSelf", "emOther", "socialUnderstanding"] }
];

function applyDifficultyBoost(score, difficulty) {
  if (difficulty === 3) return Math.min(score + 0.25, 7);
  if (difficulty === 5) return Math.min(score + 0.5, 7);
  return score;
}

function average(arr) {
  return arr.length === 0 ? 0 : arr.reduce((sum, x) => sum + x, 0) / arr.length;
}

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border text-sm px-2 py-1 rounded shadow">
        <div><strong>Avg Score:</strong> {payload[0].value.toFixed(2)}</div>
      </div>
    );
  }
  return null;
};

const LeadershipBadgesWidget = () => {
  const [badgeData, setBadgeData] = useState({});
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_BASE_URL}/api/dashboard/badges`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`
      }
    })
      .then(res => res.json())
      .then(data => {
        console.log("🏅 Full badge data:", data);
        setBadgeData(data);
      })
      .catch(err => console.error("Failed to load badge data:", err));
  }, []);

  const getBadgeImage = (prefix, level) => {
    const award = level || 'bronze';
    return `/badges/${prefix}_${award}.png`;
  };

  const renderSparkline = (domainKey, domainInfo) => {
    const conversations = badgeData?.conversations || {};
    const growth = badgeData?.growthBadges?.[domainKey] || {};

    if (!growth) return <div className="text-sm text-gray-400 italic">Not enough data yet to show progress.</div>;

    const { baseline, targets = {} } = growth;
    const rawScores = [];
    const imagePrefix = domainInfo.imagePrefix;
    console.log('imagePRefix value',imagePrefix);
  
    Object.entries(conversations).forEach(([id, convo]) => {
      const avg = average(
        domainInfo.subskills.map(skill =>
          applyDifficultyBoost(convo.scores?.[skill] || 0, parseInt(convo.difficulty))
        )
      );
      let date = "unknown";
      if (convo.timestamp?.toDate) {
        date = convo.timestamp.toDate().toISOString().split('T')[0];
      } else if (typeof convo.timestamp === "number") {
        date = new Date(convo.timestamp).toISOString().split('T')[0];
      }
  
      rawScores.push({ id, date, score: parseFloat(avg.toFixed(2)) });
    });
  
    // Sort by date
    rawScores.sort((a, b) => new Date(a.date) - new Date(b.date));
  
    if (rawScores.length < 3 || !badgeData?.growthBadges?.[domainKey]) {
      return <div className="text-sm text-gray-400 italic">Not enough data yet to show progress.</div>;
    }

    // Running top-three average
    const rollingData = [];
    const seenScores = [];
  
    rawScores.forEach(entry => {
      seenScores.push(entry.score);
      const topThree = [...seenScores].sort((a, b) => b - a).slice(0, 3);
      const rollingAvg = average(topThree);
      rollingData.push({ date: entry.date, score: parseFloat(rollingAvg.toFixed(2)) });
    });
  
    return (
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={rollingData}>
          <XAxis dataKey="date" hide />
          <YAxis domain={[baseline ? Math.max(0, baseline - 1) : 0, 7]} hide />
          <Tooltip content={<CustomTooltip />} />
          <Line
            type="monotone"
            dataKey="score"
            name="Avg Score"
            stroke="#3b82f6"
            strokeWidth={2}
            dot={false}
          />

          {baseline && (
            <ReferenceLine
              y={baseline}
              stroke="#888"
              strokeDasharray="3 3"
              label={({ viewBox }) => (
                <text
                  x={viewBox.width / 2 - 20}
                  y={viewBox.y - 7}
                  fill="#888888"
                  fontSize="16"
                  textAnchor="start"
                  alignmentBaseline="middle"
                >
                  Baseline
                </text>
              )}
            />
          )}

          {targets.bronze && (
            <ReferenceLine
              y={targets.bronze}
              stroke="#cd7f32"
              strokeDasharray="4 2"
              label={({ viewBox }) => (
                <>
                <image
                  href={`/badges/${imagePrefix}_bronze.png`}
                  x={10}
                  y={viewBox.y - 16}
                  width="14"
                  height="14"
                />
                <text
                  x={28}
                  y={viewBox.y - 5}
                  fill="#cd7f32"
                  fontSize="12"
                  alignmentBaseline="middle"
                  textAnchor="start"
                >
                  Bronze Goal
                </text>
              </>
              )}
            />
          )}

          {targets.silver && (
            <ReferenceLine
              y={targets.silver}
              stroke="#c0c0c0"
              strokeDasharray="4 2"
              label={({ viewBox }) => (
                <>
                  <image
                    href={`/badges/${domainInfo.imagePrefix}_silver.png`}
                    x={10}
                    y={viewBox.y - 16}
                    width="14"
                    height="14"
                  />
                  <text
                    x={28}
                    y={viewBox.y - 5}
                    fill="#c0c0c0"
                    fontSize="12"
                    textAnchor="start"
                    alignmentBaseline="middle"
                  >
                    Silver Goal
                  </text>
                </>
              )}
            />
          )}

          {targets.gold && (
            <ReferenceLine
              y={targets.gold}
              stroke="#ffd700"
              strokeDasharray="4 2"
              label={({ viewBox }) => (
                <>
                  <image
                    href={`/badges/${domainInfo.imagePrefix}_gold.png`}
                    x={10}
                    y={viewBox.y - 16}
                    width="14"
                    height="14"
                  />
                  <text
                    x={28}
                    y={viewBox.y - 5}
                    fill="#bfa800"
                    fontSize="12"
                    textAnchor="start"
                    alignmentBaseline="middle"
                  >
                    Gold Goal
                  </text>
                </>
              )}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    );
  };  

  return (
    <div className="w-full bg-transparent rounded p-6 mb-6">
      <div className="relative mb-4">
        <h2 className="text-2xl font-semibold text-center">Leadership Growth Badges</h2>
        <button
          onClick={() => setShowModal(true)}
          className="absolute right-0 top-1 text-sm text-blue-600 hover:underline"
        >
          How do badges work?
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 w-full">
        {badgeDomains.map(({ key, label, imagePrefix, subskills }) => {
          const awarded = badgeData?.growthBadges?.[key]?.awarded;
          return (
            <div key={key} className="flex flex-col items-center">
              {/* Badge Image Above Box */}
              <img
                src={getBadgeImage(imagePrefix, awarded)}
                alt={`${label} badge`}
                className={`w-16 h-16 mb-2 ${!awarded ? 'filter grayscale brightness-150 opacity-30' : ''}`}
              />
          
              {/* Colored Box Below */}
              <div
                className={`rounded-lg border shadow-sm flex flex-col items-center p-4 w-full ${
                  key === 'motivation' ? 'bg-green-50 border-green-200' :
                  key === 'leadershipSkills' ? 'bg-purple-50 border-purple-200' :
                  key === 'leadershipStyle' ? 'bg-blue-50 border-blue-200' :
                  'bg-yellow-50 border-yellow-200'
                }`}
              >
                <div className="text-md font-semibold text-center">{label}</div>
                <div className="w-full min-h-[250px] mt-2">
                  {renderSparkline(key, { subskills, imagePrefix })}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {showModal && (
        <Modal title="How Leadership Growth Badges Work" onClose={() => setShowModal(false)}>
          <>
            <p className="mb-2">
              These badges recognize your personal growth across the four core leadership domains: <strong>Motivation</strong>, <strong>Leadership Skills</strong>, <strong>Leadership Style</strong>, and <strong>Emotional Intelligence</strong>.
            </p>
            <p className="mb-2">
              Your progress is tracked by comparing your current performance to a baseline score (based on your first two conversations).
              Badge levels are earned based on average improvements measured by the average of your best three conversations:
            </p>
            <ul className="list-disc list-inside mb-2 pl-4">
              <li><strong>Bronze:</strong> Improvement from baseline by 2.0 points</li>
              <li><strong>Silver:</strong> Improvement from baseline by 2.5 points</li>
              <li><strong>Gold:</strong> Improvement from baseline by 3.0 points</li>
            </ul>
            <p className="mb-2">
              Higher difficulty scenarios receive a score boost: +0.25 for Intermediate, +0.5 for Advanced. Your badge reflects progress, not perfection.
            </p>
            <p className="italic text-gray-600 text-sm">
              Your baseline is capped at 3.5 to ensure continued opportunities for growth and learning.
            </p>
          </>
        </Modal>
      )}

    </div>
  );
};

export default LeadershipBadgesWidget;